import { useState, useEffect, useRef } from 'react';
import { COLORS } from '../lib/colors';

// ── Ebook email-gate modal ────────────────────────────────────────────────────
export default function EbookModal({ ebook, onClose }) {
  const [email, setEmail]   = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [errMsg, setErrMsg] = useState('');

  const dialogRef  = useRef(null);
  const closeRef   = useRef(null);
  const abortRef   = useRef(null);
  const triggerRef = useRef(null);

  const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

  // Move focus into dialog on open; lock background scroll; on close, restore
  // both the scroll and the focus to whatever invoked the modal.
  useEffect(() => {
    triggerRef.current = document.activeElement;
    closeRef.current?.focus();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      abortRef.current?.abort();
      document.body.style.overflow = prevOverflow;
      triggerRef.current?.focus?.();
    };
  }, []);

  // Escape to close + Tab focus trap
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key !== 'Tab') return;
      const focusable = dialogRef.current?.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable || focusable.length === 0) return;
      const first = focusable[0];
      const last  = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last)  { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValidEmail(email)) {
      setErrMsg('Ingresa un correo válido.');
      return;
    }
    setStatus('loading');
    setErrMsg('');

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const res  = await fetch('/api/ebook.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), ebook: ebook.key }),
        signal: controller.signal,
      });
      const data = await res.json();
      if (data.success) {
        setStatus('success');
      } else {
        setErrMsg(data.error || 'Ocurrió un error, intenta de nuevo.');
        setStatus('error');
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      setErrMsg('Ocurrió un error de conexión, intenta de nuevo.');
      setStatus('error');
    }
  };

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 999,
        background: 'rgba(15,20,25,0.7)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: '20px', overflowY: 'auto',
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ebook-modal-title"
        style={{
          background: COLORS.white, maxWidth: 480, width: '100%',
          padding: '40px 40px 36px', position: 'relative',
          margin: 'auto',
        }}
      >
        {/* Close */}
        <button
          ref={closeRef}
          onClick={onClose}
          style={{
            position: 'absolute', top: 8, right: 8,
            width: 44, height: 44,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'none', border: 'none', cursor: 'pointer',
            color: COLORS.textLight, lineHeight: 1, fontSize: 22,
          }}
          aria-label="Cerrar modal"
        >×</button>

        {status === 'success' ? (
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <p id="ebook-modal-title" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400, fontSize: 26, color: COLORS.navy, margin: '0 0 12px' }}>
              ¡Casi listo!
            </p>
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 15, color: COLORS.textLight, lineHeight: 1.7, margin: '0 0 28px' }}>
              Enviamos un correo de confirmación a <strong>{email}</strong>.<br />
              Haz clic en el enlace del correo para recibir tu ebook.
            </p>
            <button
              onClick={onClose}
              style={{
                fontFamily: "'Inter', sans-serif", fontSize: 12, fontWeight: 500,
                letterSpacing: '1.2px', textTransform: 'uppercase', lineHeight: '44px',
                color: COLORS.cream, background: COLORS.navy,
                border: 'none', padding: '0 32px', cursor: 'pointer',
              }}
            >CERRAR</button>
          </div>
        ) : (
          <>
            <p style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, fontWeight: 400, letterSpacing: '2.2px', textTransform: 'uppercase', color: COLORS.goldText, margin: '0 0 8px' }}>EBOOK</p>
            <p id="ebook-modal-title" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400, fontSize: 22, color: COLORS.navy, margin: '0 0 24px', lineHeight: 1.3 }}>
              {ebook.title}
            </p>
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 14, color: COLORS.textLight, margin: '0 0 24px', lineHeight: 1.7 }}>
              Ingresa tu correo y te enviamos un enlace para confirmar la descarga.
            </p>
            <form onSubmit={handleSubmit}>
              <label
                htmlFor="ebook-email-input"
                style={{
                  display: 'block', fontFamily: "'Inter', sans-serif",
                  fontSize: 13, color: COLORS.textLight, marginBottom: 6,
                }}
              >
                Correo electrónico
              </label>
              <input
                id="ebook-email-input"
                name="email"
                autoComplete="email"
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setErrMsg(''); }}
                placeholder="tu@correo.com"
                required
                disabled={status === 'loading'}
                aria-describedby={errMsg ? 'ebook-email-error' : undefined}
                style={{
                  display: 'block', width: '100%', boxSizing: 'border-box',
                  fontFamily: "'Inter', sans-serif", fontSize: 15,
                  color: COLORS.text, border: `1px solid ${errMsg ? COLORS.error : COLORS.border}`,
                  padding: '12px 16px', marginBottom: errMsg ? 8 : 20,
                  outline: 'none', background: COLORS.white,
                  opacity: status === 'loading' ? 0.6 : 1,
                }}
              />
              {errMsg && (
                <p
                  id="ebook-email-error"
                  role="alert"
                  style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: COLORS.error, margin: '0 0 16px' }}
                >{errMsg}</p>
              )}
              <button
                type="submit"
                disabled={status === 'loading'}
                style={{
                  fontFamily: "'Inter', sans-serif", fontSize: 12, fontWeight: 500,
                  letterSpacing: '1.2px', textTransform: 'uppercase', lineHeight: '44px',
                  color: COLORS.cream,
                  background: status === 'loading' ? COLORS.textLight : COLORS.navy,
                  border: 'none', padding: '0 32px', cursor: status === 'loading' ? 'default' : 'pointer',
                  width: '100%', transition: 'opacity 0.2s',
                }}
              >
                {status === 'loading' ? 'ENVIANDO...' : 'RECIBIR EBOOK'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
